import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  TrendingUp, Users, LayoutGrid, Crown,
  Download, FileText, DollarSign, Clock,
  BarChart3, Layers,
} from 'lucide-react'
import {
  AreaChart, Area, XAxis, Tooltip as RechartsTooltip,
  ResponsiveContainer, PieChart, Pie, Cell,
  BarChart, Bar, YAxis, CartesianGrid,
} from 'recharts'
import { cn } from '@/lib/utils'
import { fadeInUp, cardReveal } from '@/lib/motion'
import { useToast } from '@/components/shared/Toast'

const periods = ['今日', '本週', '本月', '自訂'] as const
type Period = typeof periods[number]

// -- Period-based mock data --

interface RevenueItem {
  label: string
  sub: string
  amount: string
  pct: number
  color: string
  chartColor: string
}

interface StatItem {
  label: string
  value: string
  icon: typeof Users
  color: string
}

interface AgentItem {
  rank: number
  name: string
  commission: string
  gradient: string
}

interface RoomHandsItem {
  name: string
  hands: number
}

interface PeriodData {
  total: string
  revenue: RevenueItem[]
  players: StatItem[]
  rooms: StatItem[]
  agents: AgentItem[]
  revenueTrend: number[]
  revenueTrendLabels: string[]
  playerActivity: number[]
  playerActivityLabels: string[]
  topRooms: RoomHandsItem[]
}

const dataByPeriod: Record<Period, PeriodData> = {
  '今日': {
    total: '$1,847.50',
    revenue: [
      { label: '抽水收入', sub: 'Rake', amount: '$1,205.00', pct: 65.2, color: 'bg-gold', chartColor: '#d4a843' },
      { label: '代理佣金', sub: 'Agent Commission', amount: '$336.50', pct: 18.2, color: 'bg-violet-500', chartColor: '#8b5cf6' },
      { label: '手續費', sub: 'Fees', amount: '$157.00', pct: 8.5, color: 'bg-cyan-500', chartColor: '#06b6d4' },
      { label: '累積獎金貢獻', sub: 'Jackpot Contribution', amount: '$149.00', pct: 8.1, color: 'bg-emerald-500', chartColor: '#10b981' },
    ],
    players: [
      { label: '新增用戶', value: '3', icon: Users, color: 'text-emerald-400 bg-emerald-500/10' },
      { label: '活躍用戶', value: '47', icon: Users, color: 'text-blue-400 bg-blue-500/10' },
      { label: '平均遊玩時間', value: '38 分鐘', icon: Clock, color: 'text-amber-400 bg-amber-500/10' },
      { label: '每用戶營收', value: '$39.30', icon: DollarSign, color: 'text-gold bg-gold/10' },
    ],
    rooms: [
      { label: '總開桌數', value: '8', icon: LayoutGrid, color: 'text-violet-400 bg-violet-500/10' },
      { label: '總手數', value: '124', icon: Layers, color: 'text-cyan-400 bg-cyan-500/10' },
      { label: '平均每桌手數', value: '15.5', icon: BarChart3, color: 'text-emerald-400 bg-emerald-500/10' },
      { label: '最熱門盲注', value: '1/2', icon: TrendingUp, color: 'text-gold bg-gold/10' },
    ],
    agents: [
      { rank: 1, name: 'PokerKing', commission: '$185.00', gradient: 'from-amber-500 to-orange-600' },
      { rank: 2, name: 'Sarah_HK', commission: '$102.50', gradient: 'from-violet-500 to-purple-600' },
      { rank: 3, name: 'TW_Agent', commission: '$49.00', gradient: 'from-cyan-500 to-blue-600' },
    ],
    revenueTrend: [120, 180, 150, 220, 280, 350, 547],
    revenueTrendLabels: ['6am', '8am', '10am', '12pm', '2pm', '4pm', '6pm'],
    playerActivity: [12, 18, 25, 34, 42, 47, 38],
    playerActivityLabels: ['6am', '8am', '10am', '12pm', '2pm', '4pm', '6pm'],
    topRooms: [
      { name: 'A1F3', hands: 42 },
      { name: 'B2G4', hands: 34 },
      { name: 'D4J6', hands: 24 },
      { name: 'F6L8', hands: 14 },
      { name: 'H8N2', hands: 10 },
    ],
  },
  '本週': {
    total: '$12,847.50',
    revenue: [
      { label: '抽水收入', sub: 'Rake', amount: '$8,420.00', pct: 65.5, color: 'bg-gold', chartColor: '#d4a843' },
      { label: '代理佣金', sub: 'Agent Commission', amount: '$2,340.00', pct: 18.2, color: 'bg-violet-500', chartColor: '#8b5cf6' },
      { label: '手續費', sub: 'Fees', amount: '$1,087.50', pct: 8.5, color: 'bg-cyan-500', chartColor: '#06b6d4' },
      { label: '累積獎金貢獻', sub: 'Jackpot Contribution', amount: '$1,000.00', pct: 7.8, color: 'bg-emerald-500', chartColor: '#10b981' },
    ],
    players: [
      { label: '新增用戶', value: '12', icon: Users, color: 'text-emerald-400 bg-emerald-500/10' },
      { label: '活躍用戶', value: '134', icon: Users, color: 'text-blue-400 bg-blue-500/10' },
      { label: '平均遊玩時間', value: '42 分鐘', icon: Clock, color: 'text-amber-400 bg-amber-500/10' },
      { label: '每用戶營收', value: '$38.50', icon: DollarSign, color: 'text-gold bg-gold/10' },
    ],
    rooms: [
      { label: '總開桌數', value: '28', icon: LayoutGrid, color: 'text-violet-400 bg-violet-500/10' },
      { label: '總手數', value: '847', icon: Layers, color: 'text-cyan-400 bg-cyan-500/10' },
      { label: '平均每桌手數', value: '30.3', icon: BarChart3, color: 'text-emerald-400 bg-emerald-500/10' },
      { label: '最熱門盲注', value: '1/2', icon: TrendingUp, color: 'text-gold bg-gold/10' },
    ],
    agents: [
      { rank: 1, name: 'PokerKing', commission: '$2,150.00', gradient: 'from-amber-500 to-orange-600' },
      { rank: 2, name: 'Sarah_HK', commission: '$1,285.00', gradient: 'from-violet-500 to-purple-600' },
      { rank: 3, name: 'TW_Agent', commission: '$875.00', gradient: 'from-cyan-500 to-blue-600' },
    ],
    revenueTrend: [1520, 1890, 1340, 2100, 1750, 2400, 1847],
    revenueTrendLabels: ['一', '二', '三', '四', '五', '六', '日'],
    playerActivity: [35, 42, 38, 55, 48, 62, 47],
    playerActivityLabels: ['一', '二', '三', '四', '五', '六', '日'],
    topRooms: [
      { name: 'A1F3', hands: 245 },
      { name: 'B2G4', hands: 198 },
      { name: 'D4J6', hands: 156 },
      { name: 'F6L8', hands: 124 },
      { name: 'H8N2', hands: 98 },
    ],
  },
  '本月': {
    total: '$48,320.00',
    revenue: [
      { label: '抽水收入', sub: 'Rake', amount: '$31,408.00', pct: 65.0, color: 'bg-gold', chartColor: '#d4a843' },
      { label: '代理佣金', sub: 'Agent Commission', amount: '$8,697.60', pct: 18.0, color: 'bg-violet-500', chartColor: '#8b5cf6' },
      { label: '手續費', sub: 'Fees', amount: '$4,348.80', pct: 9.0, color: 'bg-cyan-500', chartColor: '#06b6d4' },
      { label: '累積獎金貢獻', sub: 'Jackpot Contribution', amount: '$3,865.60', pct: 8.0, color: 'bg-emerald-500', chartColor: '#10b981' },
    ],
    players: [
      { label: '新增用戶', value: '45', icon: Users, color: 'text-emerald-400 bg-emerald-500/10' },
      { label: '活躍用戶', value: '312', icon: Users, color: 'text-blue-400 bg-blue-500/10' },
      { label: '平均遊玩時間', value: '48 分鐘', icon: Clock, color: 'text-amber-400 bg-amber-500/10' },
      { label: '每用戶營收', value: '$42.80', icon: DollarSign, color: 'text-gold bg-gold/10' },
    ],
    rooms: [
      { label: '總開桌數', value: '92', icon: LayoutGrid, color: 'text-violet-400 bg-violet-500/10' },
      { label: '總手數', value: '3,241', icon: Layers, color: 'text-cyan-400 bg-cyan-500/10' },
      { label: '平均每桌手數', value: '35.2', icon: BarChart3, color: 'text-emerald-400 bg-emerald-500/10' },
      { label: '最熱門盲注', value: '2/5', icon: TrendingUp, color: 'text-gold bg-gold/10' },
    ],
    agents: [
      { rank: 1, name: 'PokerKing', commission: '$8,420.00', gradient: 'from-amber-500 to-orange-600' },
      { rank: 2, name: 'Sarah_HK', commission: '$5,120.00', gradient: 'from-violet-500 to-purple-600' },
      { rank: 3, name: 'TW_Agent', commission: '$3,250.00', gradient: 'from-cyan-500 to-blue-600' },
    ],
    revenueTrend: [9800, 11200, 10500, 12400, 11800, 14200, 13800],
    revenueTrendLabels: ['W1', 'W2', 'W3', 'W4', 'W5', 'W6', 'W7'],
    playerActivity: [180, 220, 245, 280, 310, 290, 312],
    playerActivityLabels: ['W1', 'W2', 'W3', 'W4', 'W5', 'W6', 'W7'],
    topRooms: [
      { name: 'A1F3', hands: 820 },
      { name: 'B2G4', hands: 685 },
      { name: 'D4J6', hands: 542 },
      { name: 'F6L8', hands: 410 },
      { name: 'H8N2', hands: 312 },
    ],
  },
  '自訂': {
    total: '$12,847.50',
    revenue: [
      { label: '抽水收入', sub: 'Rake', amount: '$8,420.00', pct: 65.5, color: 'bg-gold', chartColor: '#d4a843' },
      { label: '代理佣金', sub: 'Agent Commission', amount: '$2,340.00', pct: 18.2, color: 'bg-violet-500', chartColor: '#8b5cf6' },
      { label: '手續費', sub: 'Fees', amount: '$1,087.50', pct: 8.5, color: 'bg-cyan-500', chartColor: '#06b6d4' },
      { label: '累積獎金貢獻', sub: 'Jackpot Contribution', amount: '$1,000.00', pct: 7.8, color: 'bg-emerald-500', chartColor: '#10b981' },
    ],
    players: [
      { label: '新增用戶', value: '12', icon: Users, color: 'text-emerald-400 bg-emerald-500/10' },
      { label: '活躍用戶', value: '47', icon: Users, color: 'text-blue-400 bg-blue-500/10' },
      { label: '平均遊玩時間', value: '42 分鐘', icon: Clock, color: 'text-amber-400 bg-amber-500/10' },
      { label: '每用戶營收', value: '$38.50', icon: DollarSign, color: 'text-gold bg-gold/10' },
    ],
    rooms: [
      { label: '總開桌數', value: '28', icon: LayoutGrid, color: 'text-violet-400 bg-violet-500/10' },
      { label: '總手數', value: '847', icon: Layers, color: 'text-cyan-400 bg-cyan-500/10' },
      { label: '平均每桌手數', value: '30.3', icon: BarChart3, color: 'text-emerald-400 bg-emerald-500/10' },
      { label: '最熱門盲注', value: '1/2', icon: TrendingUp, color: 'text-gold bg-gold/10' },
    ],
    agents: [
      { rank: 1, name: 'PokerKing', commission: '$2,150.00', gradient: 'from-amber-500 to-orange-600' },
      { rank: 2, name: 'Sarah_HK', commission: '$1,285.00', gradient: 'from-violet-500 to-purple-600' },
      { rank: 3, name: 'TW_Agent', commission: '$875.00', gradient: 'from-cyan-500 to-blue-600' },
    ],
    revenueTrend: [1520, 1890, 1340, 2100, 1750, 2400, 1847],
    revenueTrendLabels: ['一', '二', '三', '四', '五', '六', '日'],
    playerActivity: [35, 42, 38, 55, 48, 62, 47],
    playerActivityLabels: ['一', '二', '三', '四', '五', '六', '日'],
    topRooms: [
      { name: 'A1F3', hands: 245 },
      { name: 'B2G4', hands: 198 },
      { name: 'D4J6', hands: 156 },
      { name: 'F6L8', hands: 124 },
      { name: 'H8N2', hands: 98 },
    ],
  },
}

// Custom tooltip for dark theme
function ChartTooltip({ active, payload, label }: { active?: boolean; payload?: Array<{ value: number }>; label?: string }) {
  if (!active || !payload?.length) return null
  return (
    <div className="rounded-lg px-3 py-2 text-xs shadow-lg bg-surface-elevated border border-foreground/10">
      <p className="text-muted-foreground mb-0.5">{label}</p>
      <p className="text-foreground font-bold font-mono">${payload[0].value.toLocaleString()}</p>
    </div>
  )
}

function PlayerTooltip({ active, payload, label }: { active?: boolean; payload?: Array<{ value: number }>; label?: string }) {
  if (!active || !payload?.length) return null
  return (
    <div className="rounded-lg px-3 py-2 text-xs shadow-lg bg-surface-elevated border border-foreground/10">
      <p className="text-muted-foreground mb-0.5">{label}</p>
      <p className="text-foreground font-bold font-mono">{payload[0].value} players</p>
    </div>
  )
}

function RoomTooltip({ active, payload }: { active?: boolean; payload?: Array<{ value: number; payload: RoomHandsItem }> }) {
  if (!active || !payload?.length) return null
  return (
    <div className="rounded-lg px-3 py-2 text-xs shadow-lg bg-surface-elevated border border-foreground/10">
      <p className="text-muted-foreground mb-0.5">Room {payload[0].payload.name}</p>
      <p className="text-foreground font-bold font-mono">{payload[0].value} hands</p>
    </div>
  )
}

export function AdminReportsScreen() {
  const [period, setPeriod] = useState<Period>('本月')
  const navigate = useNavigate()
  const { toast } = useToast()

  const data = dataByPeriod[period]

  // Prepare chart data
  const revenueTrendData = data.revenueTrend.map((value, i) => ({
    label: data.revenueTrendLabels[i],
    value,
  }))

  const donutData = data.revenue.map((item) => ({
    name: item.label,
    value: item.pct,
    color: item.chartColor,
    amount: item.amount,
  }))

  const playerActivityData = data.playerActivity.map((value, i) => ({
    label: data.playerActivityLabels[i],
    value,
  }))

  function handleExportCSV() {
    toast({ type: 'success', title: 'CSV 報表已下載', description: `${period}報表已匯出為 CSV 格式` })
  }

  function handleExportPDF() {
    toast({ type: 'success', title: 'PDF 報表已產生', description: `${period}報表已匯出為 PDF 格式` })
  }

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={{ hidden: { opacity: 0 }, visible: { opacity: 1, transition: { staggerChildren: 0.06 } } }}
      className="p-4 pb-8 flex flex-col gap-5"
    >
      {/* Date Range Selector */}
      <motion.div variants={fadeInUp} className="flex gap-1 bg-surface-elevated rounded-xl p-1">
        {periods.map((p) => (
          <button
            key={p}
            onClick={() => setPeriod(p)}
            className={cn(
              'flex-1 py-2 rounded-lg text-xs font-bold transition-colors',
              period === p ? 'bg-gold/20 text-gold' : 'text-muted-foreground'
            )}
          >
            {p}
          </button>
        ))}
      </motion.div>

      {/* Revenue Report Card */}
      <motion.section variants={fadeInUp} className="p-4 rounded-2xl glass-card space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-bold text-foreground">營收報表</h3>
          <div className="w-8 h-8 rounded-lg bg-gold/10 flex items-center justify-center">
            <TrendingUp className="w-4 h-4 text-gold" />
          </div>
        </div>
        <div className="border-b border-foreground/10 pb-4">
          <p className="text-[10px] text-muted-foreground mb-1">期間總計</p>
          <p className="text-2xl font-bold font-mono text-gold">{data.total}</p>
        </div>

        {/* Revenue Trend AreaChart */}
        <div>
          <p className="text-[10px] text-muted-foreground mb-2">營收趨勢</p>
          <div className="h-32">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={revenueTrendData} margin={{ top: 4, right: 4, bottom: 0, left: 4 }}>
                <defs>
                  <linearGradient id="goldGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#d4a843" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="#d4a843" stopOpacity={0.05} />
                  </linearGradient>
                </defs>
                <XAxis
                  dataKey="label"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 10 }}
                />
                <RechartsTooltip content={<ChartTooltip />} />
                <Area
                  type="monotone"
                  dataKey="value"
                  stroke="#d4a843"
                  strokeWidth={2}
                  fill="url(#goldGradient)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Revenue Composition Donut Chart */}
        <div>
          <p className="text-[10px] text-muted-foreground mb-2">營收組成</p>
          <div className="flex items-center gap-4">
            <div className="relative w-[140px] h-[140px] flex-shrink-0">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={donutData}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={70}
                    dataKey="value"
                    stroke="none"
                  >
                    {donutData.map((entry, index) => (
                      <Cell key={index} fill={entry.color} />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
              {/* Center total label */}
              <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                <p className="text-[9px] text-muted-foreground">總計</p>
                <p className="text-xs font-bold font-mono text-gold">{data.total}</p>
              </div>
            </div>
            {/* Legend */}
            <div className="flex-1 space-y-2">
              {data.revenue.map((item) => (
                <div key={item.label} className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: item.chartColor }} />
                    <span className="text-[10px] text-foreground truncate">{item.label}</span>
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <span className="text-[10px] font-bold font-mono text-foreground">{item.amount}</span>
                    <span className="text-[9px] text-muted-foreground w-8 text-right">{item.pct}%</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </motion.section>

      {/* Player Activity Card */}
      <motion.section variants={fadeInUp} className="space-y-2">
        <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wider px-1">玩家活動</h3>
        <div className="grid grid-cols-2 gap-3">
          {data.players.map((s) => (
            <motion.div key={s.label} variants={cardReveal} className="p-3.5 rounded-2xl glass-card space-y-2">
              <div className={cn('w-8 h-8 rounded-lg flex items-center justify-center', s.color)}>
                <s.icon className="w-4 h-4" />
              </div>
              <div>
                <p className="text-lg font-bold font-mono text-foreground">{s.value}</p>
                <p className="text-[10px] text-muted-foreground">{s.label}</p>
              </div>
            </motion.div>
          ))}
        </div>
        {/* Player Activity BarChart */}
        <motion.div variants={cardReveal} className="p-4 rounded-2xl glass-card">
          <p className="text-[10px] text-muted-foreground mb-2">每日活躍玩家</p>
          <div className="h-28">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={playerActivityData} margin={{ top: 4, right: 4, bottom: 0, left: 4 }}>
                <XAxis
                  dataKey="label"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 10 }}
                />
                <RechartsTooltip content={<PlayerTooltip />} />
                <Bar dataKey="value" radius={[4, 4, 0, 0]} fill="#3b82f6" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </motion.div>
      </motion.section>

      {/* Room Activity Card */}
      <motion.section variants={fadeInUp} className="space-y-2">
        <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wider px-1">房間活動</h3>
        <div className="grid grid-cols-2 gap-3">
          {data.rooms.map((s) => (
            <motion.div key={s.label} variants={cardReveal} className="p-3.5 rounded-2xl glass-card space-y-2">
              <div className={cn('w-8 h-8 rounded-lg flex items-center justify-center', s.color)}>
                <s.icon className="w-4 h-4" />
              </div>
              <div>
                <p className="text-lg font-bold font-mono text-foreground">{s.value}</p>
                <p className="text-[10px] text-muted-foreground">{s.label}</p>
              </div>
            </motion.div>
          ))}
        </div>
        {/* Hands per Room Horizontal BarChart */}
        <motion.div variants={cardReveal} className="p-4 rounded-2xl glass-card">
          <p className="text-[10px] text-muted-foreground mb-2">房間手數排行</p>
          <div className="h-36">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={data.topRooms}
                layout="vertical"
                margin={{ top: 0, right: 4, bottom: 0, left: 4 }}
              >
                <CartesianGrid horizontal={false} stroke="hsl(var(--border))" />
                <XAxis type="number" hide />
                <YAxis
                  type="category"
                  dataKey="name"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 10 }}
                  width={36}
                />
                <RechartsTooltip content={<RoomTooltip />} />
                <Bar dataKey="hands" radius={[0, 4, 4, 0]} fill="#d4a843" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </motion.div>
      </motion.section>

      {/* Agent Performance Card */}
      <motion.section variants={fadeInUp} className="p-4 rounded-2xl glass-card space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-bold text-foreground">代理績效排行</h3>
          <div className="w-8 h-8 rounded-lg bg-amber-500/10 flex items-center justify-center">
            <Crown className="w-4 h-4 text-amber-400" />
          </div>
        </div>
        {data.agents.map((agent) => (
          <motion.button
            key={agent.rank}
            variants={cardReveal}
            onClick={() => navigate('/admin/agents')}
            className="w-full text-left flex items-center gap-3 p-3 rounded-xl bg-surface-elevated active:scale-[0.98] transition-transform"
          >
            <div className={cn('w-10 h-10 rounded-full bg-gradient-to-br flex items-center justify-center flex-shrink-0', agent.gradient)}>
              <span className="text-xs font-bold text-white">{agent.name.slice(0, 2).toUpperCase()}</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-foreground truncate">{agent.name}</p>
              <p className="text-[10px] text-muted-foreground">排名 #{agent.rank}</p>
            </div>
            <div className="text-right">
              <p className="text-sm font-bold font-mono text-gold">{agent.commission}</p>
              <p className="text-[10px] text-muted-foreground">佣金收入</p>
            </div>
          </motion.button>
        ))}
      </motion.section>

      {/* Export Buttons */}
      <motion.div variants={fadeInUp} className="grid grid-cols-2 gap-3">
        <button
          onClick={handleExportCSV}
          className="flex items-center justify-center gap-2 py-3 rounded-xl glass-card active:scale-95 transition-transform"
        >
          <Download className="w-4 h-4 text-gold" />
          <span className="text-xs font-bold text-gold">匯出 CSV</span>
        </button>
        <button
          onClick={handleExportPDF}
          className="flex items-center justify-center gap-2 py-3 rounded-xl glass-card active:scale-95 transition-transform"
        >
          <FileText className="w-4 h-4 text-gold" />
          <span className="text-xs font-bold text-gold">匯出 PDF</span>
        </button>
      </motion.div>
    </motion.div>
  )
}
